import { useEffect, useRef, useState, useMemo } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Search, Instagram, Globe as GlobeIcon, List, Calendar, MapPin, ExternalLink } from "lucide-react";
import { useEventsData } from "@/hooks/useEventsData";
import Tile from "@/components/shared/Tile";
import { cn } from "@/lib/utils";
import SectionTitle from "@/components/layout/SectionTitle";

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

// Region mapping based on countries
const COUNTRY_TO_REGION: Record<string, string> = {
  // Europe
  Germany: "Europe",
  France: "Europe",
  Italy: "Europe",
  Spain: "Europe",
  "United Kingdom": "Europe",
  UK: "Europe",
  Netherlands: "Europe",
  Belgium: "Europe",
  Switzerland: "Europe",
  Austria: "Europe",
  Poland: "Europe",
  Portugal: "Europe",
  Greece: "Europe",
  Sweden: "Europe",
  Norway: "Europe",
  Denmark: "Europe",
  Finland: "Europe",
  Ireland: "Europe",
  "Czech Republic": "Europe",
  Hungary: "Europe",
  Romania: "Europe",
  Croatia: "Europe",
  Bulgaria: "Europe",
  Serbia: "Europe",
  
  // Latin America
  Mexico: "Latin America",
  Brazil: "Latin America",
  Argentina: "Latin America",
  Colombia: "Latin America",
  Chile: "Latin America",
  Peru: "Latin America",
  Venezuela: "Latin America",
  Ecuador: "Latin America",
  Guatemala: "Latin America",
  Cuba: "Latin America",
  Haiti: "Latin America",
  "Dominican Republic": "Latin America",
  Honduras: "Latin America",
  Bolivia: "Latin America",
  "El Salvador": "Latin America",
  Nicaragua: "Latin America",
  Paraguay: "Latin America",
  "Costa Rica": "Latin America",
  Panama: "Latin America",
  Uruguay: "Latin America",
  
  // North America
  "United States": "North America",
  USA: "North America",
  US: "North America",
  Canada: "North America",
  
  // Asia
  China: "Asia",
  Japan: "Asia",
  India: "Asia",
  "South Korea": "Asia",
  Indonesia: "Asia",
  Thailand: "Asia",
  Vietnam: "Asia",
  Philippines: "Asia",
  Malaysia: "Asia",
  Singapore: "Asia",
  Taiwan: "Asia",
  "Hong Kong": "Asia",
  
  // Middle East
  Turkey: "Middle East",
  Israel: "Middle East",
  "United Arab Emirates": "Middle East",
  UAE: "Middle East",
  "Saudi Arabia": "Middle East",
  Lebanon: "Middle East",
  
  // Africa
  "South Africa": "Africa",
  Morocco: "Africa",
  Kenya: "Africa",
  Nigeria: "Africa",
  Egypt: "Africa",
  
  // Oceania
  Australia: "Oceania",
  "New Zealand": "Oceania",
};

// Calendar export utility functions
const generateGoogleCalendarUrl = (event: EventPoint): string => {
  const startDate = new Date(event.date_start);
  const endDate = new Date(event.date_end);
  
  const formatGoogleDate = (date: Date) => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };
  
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}`,
    details: `Event Type: ${event.event_type || 'Event'}${event.instagram ? `\nInstagram: ${event.instagram}` : ''}`,
    location: `${event.city}, ${event.country}`,
    ctz: 'UTC'
  });
  
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const generateICalFile = (event: EventPoint): void => {
  const startDate = new Date(event.date_start);
  const endDate = new Date(event.date_end);
  
  const formatICalDate = (date: Date) => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };
  
  const icalContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Klau y Ros//Events//EN',
    'BEGIN:VEVENT',
    `UID:${event.id}@klauyros.com`,
    `DTSTAMP:${formatICalDate(new Date())}`,
    `DTSTART:${formatICalDate(startDate)}`,
    `DTEND:${formatICalDate(endDate)}`,
    `SUMMARY:${event.title}`,
    `DESCRIPTION:Event Type: ${event.event_type || 'Event'}${event.instagram ? '\\nInstagram: ' + event.instagram : ''}`,
    `LOCATION:${event.city}, ${event.country}`,
    'STATUS:CONFIRMED',
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');
  
  const blob = new Blob([icalContent], { type: 'text/calendar;charset=utf-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${event.title.replace(/[^a-z0-9]/gi, '_')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

interface EventPoint {
  id: string;
  title: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  date_start: string;
  date_end: string;
  event_type?: 'Festival' | 'Exclusive Training' | 'Other' | null;
  instagram?: string;
  flyer_url?: string;
  region?: string;
}

interface ClusterPoint extends EventPoint {
  isCluster?: boolean;
  clusterSize?: number;
  clusterEvents?: EventPoint[];
}

export default function EventsMapboxGlobe() {
  const { events: rawEvents, loading } = useEventsData({
    enableCache: true,
    autoRefresh: true,
    refreshInterval: 300000,
  });
  
  // Enrich events with derived event_type and region if not present
  const events = useMemo(() => {
    return rawEvents.map((event) => {
      // Use eventType from JSON (which is normalized to event_type in useEventsData)
      // Map "Festivals" (with 's') to "Festival" (singular) for filter matching
      let eventType = event.event_type;
      
      // Normalize event type values from JSON
      if (eventType) {
        const normalizedType = eventType.trim();
        // Map "Festivals" to "Festival" for consistency with filter buttons
        if (normalizedType === "Festivals" || normalizedType === "Festival") {
          eventType = "Festival";
        } else if (normalizedType === "Exclusive Training" || normalizedType === "ExclusiveTraining") {
          eventType = "Exclusive Training";
        } else {
          // Default to "Other" for unknown types
          eventType = "Other";
        }
      } else {
        // Fallback: derive from title keywords if eventType is not present
        const titleLower = event.title.toLowerCase();
        if (titleLower.includes("festival") || titleLower.includes("fest")) {
          eventType = "Festival";
        } else if (titleLower.includes("training") || titleLower.includes("workshop") || titleLower.includes("exclusive")) {
          eventType = "Exclusive Training";
        } else {
          eventType = "Other";
        }
      }
      
      return {
        ...event,
        event_type: eventType as 'Festival' | 'Exclusive Training' | 'Other' | null,
        // Derive region if not present
        region: event.region || COUNTRY_TO_REGION[event.country] || "Other",
      };
    });
  }, [rawEvents]);
  
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [processedEvents, setProcessedEvents] = useState<ClusterPoint[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<ClusterPoint | null>(null);
  const [clusterView, setClusterView] = useState<EventPoint[] | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEventType, setSelectedEventType] = useState<string | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'globe' | 'list'>('globe');
  const [focusedEvents, setFocusedEvents] = useState<ClusterPoint[] | null>(null);
  const isManualInteractionRef = useRef(false);
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartRef = useRef<{
    distance: number;
    zoom: number;
    touches: Array<{ x: number; y: number }>;
  } | null>(null);

  const normalizeInstagramUrl = (instagram?: string): { url: string; display: string } | null => {
    if (!instagram) return null;
    
    let cleanUsername = instagram;
    
    if (instagram.includes('instagram.com')) {
      const match = instagram.match(/instagram\.com\/([^\/\?]+)/);
      cleanUsername = match ? match[1] : instagram;
    }
    
    cleanUsername = cleanUsername.replace('@', '').trim();
    
    if (!cleanUsername) return null;
    
    return {
      url: `https://www.instagram.com/${cleanUsername}`,
      display: 'Instagram'
    };
  };

  const clusterEventsByLocation = (events: EventPoint[]): ClusterPoint[] => {
    const locationMap = new Map<string, EventPoint[]>();
    
    events.forEach((event) => {
      // Normalize city and country for consistent matching
      const city = (event.city || '').trim();
      const country = (event.country || '').trim();
      
      if (!city || !country) {
        return;
      }
      
      const key = `${city},${country}`;
      if (!locationMap.has(key)) {
        locationMap.set(key, []);
      }
      locationMap.get(key)!.push(event);
    });
    
    const clustered: ClusterPoint[] = [];
    
    locationMap.forEach((locationEvents, locationKey) => {
      // Validate that all events in this location group have matching city and country
      const [expectedCity, expectedCountry] = locationKey.split(',');
      const validEvents = locationEvents.filter(event => {
        const city = (event.city || '').trim();
        const country = (event.country || '').trim();
        const matches = city === expectedCity && country === expectedCountry;
        
        return matches;
      });
      
      // If no valid events remain, skip this location
      if (validEvents.length === 0) {
        return;
      }
      
      if (validEvents.length >= 2) {
        const firstEvent = validEvents[0];
        // For clusters, use the most common event type or first event's type
        const eventTypes = validEvents.map(e => e.event_type).filter(Boolean) as Array<'Festival' | 'Exclusive Training' | 'Other'>;
        const mostCommonType = eventTypes.length > 0 
          ? eventTypes.reduce((a, b, _, arr) => 
              arr.filter(v => v === a).length >= arr.filter(v => v === b).length ? a : b
            ) as 'Festival' | 'Exclusive Training' | 'Other' | null
          : (firstEvent.event_type as 'Festival' | 'Exclusive Training' | 'Other' | null) || null;
        
        clustered.push({
          ...firstEvent,
          isCluster: true,
          clusterSize: validEvents.length,
          clusterEvents: validEvents,
          title: `${firstEvent.city}`,
          event_type: mostCommonType || (firstEvent.event_type as 'Festival' | 'Exclusive Training' | 'Other' | null) || null,
        });
      } else {
        clustered.push(...validEvents);
      }
    });
    
    return clustered;
  };

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current || events.length === 0) return;

    // Ensure container has dimensions before initializing
    const container = mapContainer.current;
    const rect = container.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    mapboxgl.accessToken = MAPBOX_TOKEN || '';

    try {
      map.current = new mapboxgl.Map({
        container: container,
        style: 'mapbox://styles/mapbox/dark-v11',
        center: [0, 20],
        zoom: 2,
        projection: { name: 'globe' },
        // Disable scroll zoom by default - we'll handle it manually
        scrollZoom: false,
        touchZoomRotate: false,
        touchPitch: false,
        boxZoom: false,
        doubleClickZoom: false
      });
    } catch (error) {
      // Handle WebGL initialization errors silently
      return;
    }

    map.current.on('load', () => {
      if (!map.current) return;

      // Enable globe atmosphere with very subtle orange accent
      map.current.setFog({
        color: 'rgb(45, 35, 30)',
        'high-color': 'rgb(120, 80, 60)',
        'horizon-blend': 0.02,
        'space-color': 'rgb(10, 10, 15)',
        'star-intensity': 0.2
      });

      // Track map interactions
      map.current.on('dragstart', () => {
        isManualInteractionRef.current = true;
      });
      
      map.current.on('zoomstart', () => {
        isManualInteractionRef.current = true;
      });

      // Set CSS for proper gesture handling
      const canvas = map.current.getCanvasContainer();
      canvas.style.touchAction = 'manipulation';
      canvas.style.userSelect = 'none';

      // Initial markers will be set by the useEffect that depends on searchFilteredEvents
    });

    return () => {
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
      map.current?.remove();
      map.current = null;
    };
  }, [events]);

  // Handle wheel events for trackpad gestures: Ctrl/Cmd+scroll zooms, regular scroll scrolls page
  useEffect(() => {
    // Only set up listeners when in globe view
    if (viewMode !== 'globe') return;
    
    const mapInstance = map.current;
    if (!mapInstance) return;

    const canvas = mapInstance.getCanvasContainer();
    
    const handleWheel = (e: WheelEvent) => {
      // Detect pinch-to-zoom gesture (Ctrl on Windows/Linux, Cmd on Mac)
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        
        // Manual zoom implementation for pinch-to-zoom
        const zoomDelta = e.deltaY * -0.01;
        const currentZoom = mapInstance.getZoom();
        const minZoom = 1;
        const maxZoom = 10;
        
        const newZoom = Math.max(minZoom, Math.min(maxZoom, currentZoom + zoomDelta));
        
        mapInstance.zoomTo(newZoom, {
          duration: 0 // Instant zoom for smooth trackpad feel
        });
      }
      // If not Ctrl/Cmd+scroll, let it pass through (page will scroll naturally)
    };
    
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      canvas.removeEventListener('wheel', handleWheel);
    };
  }, [viewMode, events]);

  // Handle touch gestures for mobile: pinch-to-zoom, two-finger scroll
  useEffect(() => {
    // Only set up listeners when in globe view
    if (viewMode !== 'globe') return;
    
    const mapInstance = map.current;
    if (!mapInstance) return;

    const canvas = mapInstance.getCanvasContainer();
    
    const getTouchDistance = (touches: TouchList): number => {
      if (touches.length < 2) return 0;
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };
    
    const getTouchMovementDirection = (touches: TouchList, prevTouches: Array<{ x: number; y: number }>): 'vertical' | 'horizontal' | 'none' => {
      if (touches.length < 2 || prevTouches.length < 2) return 'none';
      
      const currentY1 = touches[0].clientY;
      const currentY2 = touches[1].clientY;
      const prevY1 = prevTouches[0].y;
      const prevY2 = prevTouches[1].y;
      
      const avgDeltaY = ((currentY1 - prevY1) + (currentY2 - prevY2)) / 2;
      const avgDeltaX = ((touches[0].clientX - prevTouches[0].x) + (touches[1].clientX - prevTouches[1].x)) / 2;
      
      if (Math.abs(avgDeltaY) > Math.abs(avgDeltaX) && Math.abs(avgDeltaY) > 5) {
        return 'vertical';
      }
      if (Math.abs(avgDeltaX) > Math.abs(avgDeltaY) && Math.abs(avgDeltaX) > 5) {
        return 'horizontal';
      }
      return 'none';
    };
    
    const handleTouchStart = (e: TouchEvent) => {
      // Detect manual touch interaction (single finger drag)
      if (e.touches.length === 1) {
        isManualInteractionRef.current = true;
      }
      
      if (e.touches.length === 2) {
        const distance = getTouchDistance(e.touches);
        const currentZoom = mapInstance.getZoom();
        
        touchStartRef.current = {
          distance,
          zoom: currentZoom,
          touches: [
            { x: e.touches[0].clientX, y: e.touches[0].clientY },
            { x: e.touches[1].clientX, y: e.touches[1].clientY },
          ],
        };
        
        // Pinch-to-zoom is also manual interaction
        isManualInteractionRef.current = true;
      }
    };
    
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && touchStartRef.current) {
        const currentDistance = getTouchDistance(e.touches);
        const initialDistance = touchStartRef.current.distance;
        const distanceDelta = Math.abs(currentDistance - initialDistance);
        
        // Determine if this is a pinch (distance changing) or scroll (distance stable)
        const isPinch = distanceDelta > 10; // Threshold for pinch detection
        
        if (isPinch) {
          // Pinch gesture: zoom the map
          e.preventDefault();
          
          const zoomFactor = currentDistance / initialDistance;
          const minZoom = 1;
          const maxZoom = 10;
          
          const newZoom = Math.max(
            minZoom,
            Math.min(maxZoom, touchStartRef.current.zoom * zoomFactor)
          );
          
          mapInstance.zoomTo(newZoom, {
            duration: 0 // Instant zoom for smooth pinch feel
          });
        } else {
          // Scroll gesture: check if both touches are moving in same direction
          const currentTouches = [
            { x: e.touches[0].clientX, y: e.touches[0].clientY },
            { x: e.touches[1].clientX, y: e.touches[1].clientY },
          ];
          
          const direction = getTouchMovementDirection(e.touches, touchStartRef.current.touches);
          
          // If vertical scroll detected, allow page to scroll (don't prevent default)
          if (direction === 'vertical') {
            // Update touch positions for next check
            touchStartRef.current.touches = currentTouches;
            // Don't prevent default - let page scroll
          }
        }
      }
    };
    
    const handleTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) {
        touchStartRef.current = null;
      }
    };
    
    canvas.addEventListener('touchstart', handleTouchStart, { passive: true });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: true });
    canvas.addEventListener('touchcancel', handleTouchEnd, { passive: true });
    
    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [viewMode, events]);

  // Process events when raw events change
  useEffect(() => {
    if (events.length > 0) {
      const clustered = clusterEventsByLocation(events);
      setProcessedEvents(clustered);
    }
  }, [events]);

  const searchFilteredEvents = useMemo(() => {
    let filtered = processedEvents;
    
    // Event type filter - normalize for comparison
    // For clusters, check if any event in the cluster matches
    if (selectedEventType !== null) {
      filtered = filtered.filter(e => {
        const selectedType = selectedEventType.trim();
        
        // If it's a cluster, check if any event in the cluster matches
        if (e.isCluster && e.clusterEvents) {
          return e.clusterEvents.some(clusterEvent => {
            const eventType = clusterEvent.event_type?.trim() || '';
            return eventType === selectedType;
          });
        }
        
        // For single events, check the event type directly
        const eventType = e.event_type?.trim() || '';
        return eventType === selectedType;
      });
    }
    
    if (selectedMonth !== null) {
      filtered = filtered.filter(e => {
        if (e.isCluster && e.clusterEvents) {
          return e.clusterEvents.some(ce => new Date(ce.date_start).getMonth() === selectedMonth);
        }
        const eventMonth = new Date(e.date_start).getMonth();
        return eventMonth === selectedMonth;
      });
    }
    
    // Region filter
    if (selectedRegion !== null) {
      filtered = filtered.filter(e => e.region === selectedRegion);
    }
    
    // Country filter
    if (selectedCountry !== null) {
      filtered = filtered.filter(e => e.country === selectedCountry);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(e => {
        if (e.isCluster && e.clusterEvents) {
          return e.clusterEvents.some(ce => 
            ce.title.toLowerCase().includes(query) ||
            ce.city.toLowerCase().includes(query) ||
            ce.country.toLowerCase().includes(query) ||
            ce.event_type?.toLowerCase().includes(query)
          );
        }
        return e.title.toLowerCase().includes(query) ||
          e.city.toLowerCase().includes(query) ||
          e.country.toLowerCase().includes(query) ||
          e.event_type?.toLowerCase().includes(query);
      });
    }
    
    return filtered;
  }, [processedEvents, selectedMonth, selectedRegion, selectedCountry, searchQuery, selectedEventType]);
  
  // Compute unique filter options from events
  const filterOptions = useMemo(() => {
    const regions = new Set<string>();
    const countries = new Set<string>();
    
    events.forEach(event => {
      if (event.region) regions.add(event.region);
      if (event.country) countries.add(event.country);
    });
    
    return {
      regions: Array.from(regions).sort(),
      countries: Array.from(countries).sort(),
    };
  }, [events]);

  const formatDateRange = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (isNaN(start.getTime())) return 'Date TBA';
    
    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    
    if (!endDate || isNaN(end.getTime()) || start.getTime() === end.getTime()) {
      return startStr;
    }
    
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} - ${endStr}`;
  };

  // Update markers when filtered events change
  useEffect(() => {
    if (!map.current) return;

    // Remove existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Use filtered events for markers
    const eventsToDisplay = searchFilteredEvents;

    // Cluster events by location
    const locationMap = new Map<string, ClusterPoint[]>();
    eventsToDisplay.forEach((event) => {
      const key = `${event.city},${event.country}`;
      if (!locationMap.has(key)) {
        locationMap.set(key, []);
      }
      locationMap.get(key)!.push(event);
    });

    // Add markers for each location
    locationMap.forEach((locationEvents, key) => {
      const firstEvent = locationEvents[0];
      const [expectedCity, expectedCountry] = key.split(',');
      
      // Helper function to extract and filter events by location
      const getEventsForLocation = (event: ClusterPoint): EventPoint[] => {
        if (event.isCluster && event.clusterEvents) {
          // Filter cluster events to only include those matching the location
          return event.clusterEvents.filter(e => {
            const city = (e.city || '').trim();
            const country = (e.country || '').trim();
            return city === expectedCity && country === expectedCountry;
          });
        }
        // Single event - verify it matches location
        const city = (event.city || '').trim();
        const country = (event.country || '').trim();
        return (city === expectedCity && country === expectedCountry) ? [event] : [];
      };
      
      // Collect all events for this location, ensuring they match
      const allEvents: EventPoint[] = [];
      locationEvents.forEach(event => {
        const eventsToAdd = getEventsForLocation(event);
        allEvents.push(...eventsToAdd);
      });
      
      // If no valid events after filtering, skip this marker
      if (allEvents.length === 0) {
        return;
      }
      
      // Determine marker color based on event type
      const getEventTypeColor = (eventType?: string | null) => {
        if (eventType === 'Festival') return '#9B59B6'; // Purple
        if (eventType === 'Exclusive Training') return '#E67E22'; // Orange
        return '#F39C12'; // Yellow/Orange
      };

      const eventColor = getEventTypeColor(firstEvent.event_type);
      
      // Create marker element
      const el = document.createElement('div');
      el.className = 'events-marker';
      
      if (allEvents.length > 1) {
        // Cluster marker
        el.innerHTML = `
          <div style="
            width: 44px;
            height: 44px;
            border-radius: 50%;
            background: linear-gradient(135deg, rgba(230, 126, 34, 0.85), rgba(211, 84, 0, 0.85));
            border: 2px solid rgba(255, 255, 255, 0.9);
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            color: white;
            cursor: pointer;
            box-shadow: 0 2px 8px rgba(230, 126, 34, 0.2), 0 0 0 1px rgba(230, 126, 34, 0.1);
            backdrop-filter: blur(4px);
          ">
            ${allEvents.length}
          </div>
        `;
      } else {
        // Single event marker
        el.innerHTML = `
          <div style="
            width: 36px;
            height: 36px;
            border-radius: 50%;
            border: 2px solid rgba(255, 255, 255, 0.8);
            cursor: pointer;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3), 0 0 0 1px ${eventColor}40;
            background: linear-gradient(135deg, ${eventColor}CC, ${eventColor}99);
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <div style="
              width: 12px;
              height: 12px;
              border-radius: 50%;
              background: white;
              box-shadow: 0 0 4px ${eventColor};
            "></div>
          </div>
        `;
      }

      // Create popup content
      let popupContent = '';
      
      if (allEvents.length > 1) {
        popupContent = `
          <div style="font-family: 'Inter', sans-serif; min-width: 200px;">
            <div style="font-weight: bold; color: #E67E22; font-size: 16px; margin-bottom: 8px;">
              📍 ${firstEvent.city}, ${firstEvent.country}
            </div>
            <div style="font-size: 14px; margin-bottom: 8px;">
              <span style="background: #E67E22; padding: 4px 8px; border-radius: 4px; color: white; font-weight: bold;">
                ${allEvents.length} events
              </span>
            </div>
            <div style="font-size: 12px; opacity: 0.7; font-style: italic;">
              Click marker to view all events
            </div>
          </div>
        `;
      } else {
        const event = allEvents[0];
        const eventDate = formatDateRange(event.date_start, event.date_end);
        const eventTypeColor = getEventTypeColor(event.event_type);
        popupContent = `
          <div style="font-family: 'Inter', sans-serif; min-width: 220px;">
            ${event.flyer_url ? `
              <img 
                src="${event.flyer_url}" 
                style="width: 100%; height: auto; border-radius: 4px; margin-bottom: 8px; max-height: 120px; object-fit: cover;"
                alt="${event.title}"
              />
            ` : ''}
            <div style="font-weight: bold; color: ${eventTypeColor}; font-size: 14px; margin-bottom: 4px;">
              ${event.title}
            </div>
            <div style="font-size: 12px; margin-top: 4px; color: rgba(255, 255, 255, 0.8);">
              📅 ${eventDate}
            </div>
            <div style="font-size: 12px; margin-top: 2px; color: rgba(255, 255, 255, 0.8);">
              📍 ${event.city}, ${event.country}
            </div>
            ${event.event_type ? `
              <div style="font-size: 11px; margin-top: 6px; padding: 2px 6px; background: ${eventTypeColor}; border-radius: 4px; display: inline-block; color: white;">
                ${event.event_type}
              </div>
            ` : ''}
          </div>
        `;
      }

      const popup = new mapboxgl.Popup({ 
        offset: 25,
        closeButton: false,
        className: 'custom-popup'
      }).setHTML(popupContent);

      const marker = new mapboxgl.Marker(el)
        .setLngLat([firstEvent.longitude, firstEvent.latitude])
        .setPopup(popup)
        .addTo(map.current!);

      // Handle click - use the already-filtered allEvents array
      el.addEventListener('click', () => {
        if (allEvents.length > 1) {
          setClusterView(allEvents);
        } else {
          setFocusedEvents([allEvents[0]]);
          setSelectedEvent(allEvents[0]);
        }
      });

      markersRef.current.push(marker);
    });
  }, [searchFilteredEvents]);
  
  // Calculate center point and zoom for filtered events
  const calculateNavigationTarget = (events: ClusterPoint[]) => {
    if (events.length === 0) {
      return null;
    }
    
    // Extract valid coordinates from events (including clusters)
    const validEvents = events.filter(e => {
      const lat = e.latitude;
      const lng = e.longitude;
      return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
    });
    
    if (validEvents.length === 0) {
      return null;
    }
    
    if (validEvents.length === 1) {
      // Single event: navigate to it with close zoom
      return {
        center: [validEvents[0].longitude, validEvents[0].latitude] as [number, number],
        zoom: 6,
      };
    }
    
    // Multiple events: calculate center point and bounding box
    const lats = validEvents.map(e => e.latitude);
    const lngs = validEvents.map(e => e.longitude);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    
    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;
    
    // Calculate zoom based on span
    const latSpan = maxLat - minLat;
    const lngSpan = maxLng - minLng;
    const maxSpan = Math.max(latSpan, lngSpan);
    
    // Scale zoom based on span (larger span = lower zoom)
    let zoom = 2; // default
    if (maxSpan > 0) {
      if (maxSpan < 0.1) zoom = 8;
      else if (maxSpan < 0.5) zoom = 6;
      else if (maxSpan < 1) zoom = 4;
      else if (maxSpan < 5) zoom = 3;
      else zoom = 2;
    }
    
    return {
      center: [centerLng, centerLat] as [number, number],
      zoom,
    };
  };

  // Auto-navigate to filtered events when filters change
  useEffect(() => {
    // Skip navigation if user is manually interacting
    if (isManualInteractionRef.current) {
      return;
    }
    
    // Skip navigation if no filtered events
    if (searchFilteredEvents.length === 0) {
      return;
    }
    
    // Skip navigation if not in globe view
    if (viewMode !== 'globe') {
      return;
    }
    
    // Debounce navigation to prevent too many animations
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }
    
    navigationTimeoutRef.current = setTimeout(() => {
      const mapInstance = map.current;
      if (!mapInstance) return;
      
      // Skip if user started interacting during debounce
      if (isManualInteractionRef.current) {
        return;
      }
      
      const target = calculateNavigationTarget(searchFilteredEvents);
      
      if (target) {
        mapInstance.flyTo({
          center: target.center,
          zoom: target.zoom,
          duration: 1000
        });
      }
    }, 300); // 300ms debounce
    
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, [searchFilteredEvents, selectedMonth, selectedRegion, selectedCountry, selectedEventType, searchQuery, viewMode]);

  const eventsByMonth = useMemo(() => {
    const sortedEvents = [...searchFilteredEvents].sort((a, b) => 
      new Date(a.date_start).getTime() - new Date(b.date_start).getTime()
    );

    const grouped = new Map<string, EventPoint[]>();
    
    sortedEvents.forEach(event => {
      // Unpack cluster events for list view
      const eventsToAdd = event.isCluster && event.clusterEvents ? event.clusterEvents : [event];
      
      eventsToAdd.forEach(e => {
        const date = new Date(e.date_start);
        
        if (isNaN(date.getTime())) {
          console.warn(`Invalid date for event: ${e.title}`, e.date_start);
          return;
        }
        
        const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!grouped.has(yearMonth)) {
          grouped.set(yearMonth, []);
        }
        grouped.get(yearMonth)!.push(e);
      });
    });

    return Array.from(grouped.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([yearMonth, events]) => ({
        yearMonth,
        displayName: new Date(yearMonth + '-01').toLocaleDateString('en-US', { 
          month: 'long', 
          year: 'numeric' 
        }),
        events
      }));
  }, [searchFilteredEvents]);

  const totalEvents = searchFilteredEvents.reduce((sum, e) => sum + (e.isCluster ? e.clusterSize! : 1), 0);
  const uniqueCountries = new Set(searchFilteredEvents.map(e => e.country)).size;
  const upcomingThisMonth = searchFilteredEvents.filter(e => {
    if (e.isCluster && e.clusterEvents) {
      return e.clusterEvents.some(ce => {
        const eventDate = new Date(ce.date_start);
        const now = new Date();
        return eventDate.getMonth() === now.getMonth() && eventDate.getFullYear() === now.getFullYear();
      });
    }
    const eventDate = new Date(e.date_start);
    const now = new Date();
    return eventDate.getMonth() === now.getMonth() && eventDate.getFullYear() === now.getFullYear();
  }).length;
  const hotspots = searchFilteredEvents.filter(e => e.isCluster).length;

  const months = [
    { label: 'All', value: null },
    { label: 'Jan', value: 0 },
    { label: 'Feb', value: 1 },
    { label: 'Mar', value: 2 },
    { label: 'Apr', value: 3 },
    { label: 'May', value: 4 },
    { label: 'Jun', value: 5 },
    { label: 'Jul', value: 6 },
    { label: 'Aug', value: 7 },
    { label: 'Sep', value: 8 },
    { label: 'Oct', value: 9 },
    { label: 'Nov', value: 10 },
    { label: 'Dec', value: 11 },
  ];

  if (loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-gradient-to-b from-black via-[#0a0a0f] to-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 mx-auto mb-4" style={{ borderColor: "#E67E22" }} />
          <p className="text-white" style={{ fontFamily: "'Bensoon', sans-serif" }}>Loading events globe...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        .scrollbar-thin::-webkit-scrollbar {
          width: 8px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: #E67E22;
          border-radius: 4px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: #F39C12;
        }
        .mapboxgl-ctrl-bottom-right,
        .mapboxgl-ctrl-logo {
          display: none !important;
        }
        .mapbox-events-map .mapboxgl-canvas {
          border-radius: 16px;
        }
        .mapboxgl-ctrl-attrib {
          background: rgba(0, 0, 0, 0.8) !important;
          color: rgba(255, 255, 255, 0.6) !important;
          padding: 4px 8px !important;
          border-radius: 4px !important;
        }
        .mapboxgl-popup-content {
          background: rgba(0, 0, 0, 0.95) !important;
          border: 1px solid #E67E22 !important;
          border-radius: 8px !important;
          padding: 12px !important;
          color: white !important;
        }
        .mapboxgl-popup-tip {
          border-top-color: rgba(0, 0, 0, 0.95) !important;
        }
        .events-marker > div {
          transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
        }
        .events-marker:hover > div {
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(230, 126, 34, 0.3), 0 0 0 2px rgba(230, 126, 34, 0.2) !important;
          border-color: rgba(230, 126, 34, 0.9) !important;
        }
      `}} />
      <div className="relative w-full h-full flex flex-col bg-gradient-to-b from-black via-[#1a0f08] to-black">
        {/* Header Section - Fixed Top */}
        <div className="flex-shrink-0 w-full z-30 bg-gradient-to-b from-black/90 to-transparent backdrop-blur-sm border-b border-white/10 pb-3 pt-4">
        <div className="text-center px-4 pt-4">
          <SectionTitle
            title="Worldwide Events"
            accent="Worldwide"
            align="center"
            size="md"
            className="mb-3"
            showUnderline={true}
          />
          
          {/* Stats Row */}
          <div className="flex flex-wrap justify-center gap-4 text-white mb-4">
            <div>
              <span className="text-3xl font-bold" style={{ color: "#E67E22" }}>{totalEvents}</span>
              <span className="ml-2 text-lg text-white/70">events</span>
            </div>
            <div>
              <span className="text-3xl font-bold" style={{ color: "#E67E22" }}>{uniqueCountries}</span>
              <span className="ml-2 text-lg text-white/70">countries</span>
            </div>
            <div>
              <span className="text-3xl font-bold" style={{ color: "#E67E22" }}>{upcomingThisMonth}</span>
              <span className="ml-2 text-lg text-white/70">this month</span>
            </div>
            <div>
              <span className="text-3xl font-bold" style={{ color: "#E67E22" }}>{hotspots}</span>
              <span className="ml-2 text-lg text-white/70">hotspots</span>
            </div>
          </div>

          {/* View Mode Toggle */}
          <div className="flex flex-col sm:flex-row justify-center gap-2 mb-3 px-4">
            <button
              onClick={() => setViewMode('globe')}
              className={cn(
                'flex items-center justify-center gap-2 px-6 py-2 rounded-lg transition-all font-medium',
                viewMode === 'globe'
                  ? 'bg-[#E67E22] text-white shadow-lg'
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              )}
            >
              <GlobeIcon className="w-5 h-5" />
              <span>Globe View</span>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                'flex items-center justify-center gap-2 px-6 py-2 rounded-lg transition-all font-medium',
                viewMode === 'list'
                  ? 'bg-[#E67E22] text-white shadow-lg'
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              )}
            >
              <List className="w-5 h-5" />
              <span>List View</span>
            </button>
          </div>

          {/* Event Type Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-3 px-4">
            <button
              onClick={() => {
                setSelectedEventType(null);
                setFocusedEvents(null);
              }}
              className={cn(
                'px-6 py-2 rounded-lg transition-all font-medium text-sm',
                selectedEventType === null
                  ? 'bg-[#E67E22] text-white shadow-lg'
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              )}
            >
              All Events
            </button>
            <button
              onClick={() => {
                setSelectedEventType('Festival');
                setFocusedEvents(null);
              }}
              className={cn(
                'px-6 py-2 rounded-lg transition-all font-medium text-sm',
                selectedEventType === 'Festival'
                  ? 'bg-[#E67E22] text-white shadow-lg'
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              )}
            >
              🎉 Festivals
            </button>
            <button
              onClick={() => {
                setSelectedEventType('Exclusive Training');
                setFocusedEvents(null);
              }}
              className={cn(
                'px-6 py-2 rounded-lg transition-all font-medium text-sm',
                selectedEventType === 'Exclusive Training'
                  ? 'bg-[#E67E22] text-white shadow-lg'
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              )}
            >
              🎓 Exclusive Training
            </button>
          </div>

          {/* Month Filter Bar */}
          <div className="flex flex-wrap justify-center gap-2 px-4 max-w-5xl mx-auto overflow-x-auto mb-2">
            {months.map(month => (
              <button
                key={month.label}
                onClick={() => {
                  setSelectedMonth(month.value);
                  setFocusedEvents(null);
                }}
                className={cn(
                  'px-4 py-2 rounded-lg transition-all text-sm font-medium whitespace-nowrap',
                  selectedMonth === month.value 
                    ? 'bg-[#E67E22] text-white shadow-lg shadow-[#E67E22]/50' 
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                )}
              >
                {month.label}
              </button>
            ))}
            {(searchQuery || selectedMonth !== null || selectedEventType !== null || selectedRegion !== null || selectedCountry !== null) && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedMonth(null);
                  setSelectedEventType(null);
                  setSelectedRegion(null);
                  setSelectedCountry(null);
                }}
                className="px-4 py-2 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30 transition-all font-medium text-sm whitespace-nowrap border border-red-500/50"
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Region and Country Filters */}
          <div className="flex flex-wrap justify-center gap-3 px-4 mb-2">
            {/* Region Filter */}
            <select
              value={selectedRegion || ''}
              onChange={(e) => {
                setSelectedRegion(e.target.value || null);
                setFocusedEvents(null);
              }}
              className="px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white text-sm focus:outline-none focus:border-[#E67E22] focus:ring-2 focus:ring-[#E67E22]/20 transition-all"
              style={{ fontFamily: "'Bensoon', sans-serif", minWidth: '150px' }}
            >
              <option value="">All Regions</option>
              {filterOptions.regions.map((region) => (
                <option key={region} value={region}>
                  {region}
                </option>
              ))}
            </select>

            {/* Country Filter */}
            <select
              value={selectedCountry || ''}
              onChange={(e) => {
                setSelectedCountry(e.target.value || null);
                setFocusedEvents(null);
              }}
              className="px-4 py-2 rounded-lg bg-black/50 border border-white/10 text-white text-sm focus:outline-none focus:border-[#E67E22] focus:ring-2 focus:ring-[#E67E22]/20 transition-all"
              style={{ fontFamily: "'Bensoon', sans-serif", minWidth: '150px' }}
            >
              <option value="">All Countries</option>
              {filterOptions.countries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main Content - Conditional View */}
      {viewMode === 'globe' ? (
        <div className="flex-1 min-h-0 w-full grid grid-cols-1 xl:grid-cols-[1fr_450px] gap-0 overflow-hidden">
          {/* Left Side - Mapbox Map */}
          <div className="relative flex items-center justify-center overflow-hidden w-full h-full min-h-0">
            <div 
              ref={mapContainer}
              className="h-full w-full"
              style={{
                background: "linear-gradient(135deg, #0a0a0f 0%, #1a0a0a 50%, #0a0a0f 100%)",
              }}
            />
          </div>

          {/* Right Side - Event List */}
          <div className="relative w-full h-full border-l border-white/10 bg-black/30 backdrop-blur-md flex flex-col overflow-hidden max-md:hidden p-4">
            
            {/* Search Bar */}
            <div className="mb-4 pb-4 border-b border-white/10">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2.5 pl-10 rounded-lg bg-white/10 border border-white/20 text-white text-sm placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-[#E67E22] transition-all"
                />
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-white/50" />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-2.5 text-white/50 hover:text-white text-xs"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Focused Events Header */}
            {focusedEvents && focusedEvents.length > 0 && (
              <div className="mb-3 p-3 bg-[#E67E22]/20 border border-[#E67E22]/40 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#E67E22]" />
                    <span className="text-white text-sm font-medium">
                      {focusedEvents.length === 1 
                        ? 'Selected Event' 
                        : `${focusedEvents.length} Events at ${focusedEvents[0].city}`
                      }
                    </span>
                  </div>
                  <button
                    onClick={() => setFocusedEvents(null)}
                    className="text-xs text-white/70 hover:text-white px-2 py-1 rounded border border-white/20 hover:border-[#E67E22] transition-all"
                  >
                    Show All
                  </button>
                </div>
              </div>
            )}

            {/* Scrollable Event Tiles */}
            <div className="flex-1 min-h-0 overflow-y-auto space-y-3 scrollbar-thin scrollbar-thumb-[#E67E22] scrollbar-track-white/10 pr-2">
              {(focusedEvents || searchFilteredEvents).map((event, index) => {
                const eventsToShow = event.isCluster && event.clusterEvents ? event.clusterEvents : [event];
                return eventsToShow.map((e, idx) => (
                  <Tile
                    key={`${e.id}-${idx}`}
                    variant="glass"
                    size="md"
                    hoverable
                    noPadding
                    className={cn(
                      "cursor-pointer hover:scale-[1.02] transition-transform",
                      focusedEvents && focusedEvents.some(fe => fe.id === e.id) && "ring-2 ring-[#E67E22]"
                    )}
                    onClick={() => {
                      if (event.isCluster && event.clusterEvents) {
                        setFocusedEvents(event.clusterEvents);
                        setClusterView(event.clusterEvents);
                        if (map.current) {
                          map.current.flyTo({
                            center: [event.longitude, event.latitude],
                            zoom: 6,
                            duration: 1000
                          });
                        }
                      } else {
                        setFocusedEvents([event]);
                        setSelectedEvent(event);
                        if (map.current) {
                          map.current.flyTo({
                            center: [event.longitude, event.latitude],
                            zoom: 6,
                            duration: 1000
                          });
                        }
                      }
                    }}
                  >
                    <div className="flex flex-col p-4 h-auto">
                      {/* Image Section */}
                      {e.flyer_url ? (
                        <div className="w-full h-48 mb-4 bg-black/30 rounded-lg overflow-hidden flex items-center justify-center">
                          <img 
                            src={e.flyer_url} 
                            alt={e.title}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="w-full h-48 mb-4 rounded-lg border border-[#E67E22]/30 bg-[#E67E22]/10 flex items-center justify-center">
                          <Calendar className="w-12 h-12 text-[#E67E22]/50" />
                        </div>
                      )}
                      
                      {/* Title - Orange */}
                      <h4 className="font-bold text-base mb-3 line-clamp-2" style={{ color: '#E67E22' }}>
                        {event.isCluster ? `${event.city} (${event.clusterSize} events)` : e.title}
                      </h4>
                      
                      {/* Date */}
                      <p className="text-sm text-white/80 mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {formatDateRange(e.date_start, e.date_end)}
                      </p>
                      
                      {/* Location */}
                      <p className="text-sm text-white/80 mb-3 flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        {e.city}, {e.country}
                      </p>
                      
                      {/* Event Type Badge */}
                      {e.event_type && (
                        <span className="inline-block px-3 py-1 text-xs rounded-full mb-3 self-start"
                          style={{ background: e.event_type === "Festival" ? "#9B59B6" : "#E67E22", color: 'white' }}>
                          {e.event_type}
                        </span>
                      )}
                      
                      {/* Instagram Link */}
                      {e.instagram && (() => {
                        const igData = normalizeInstagramUrl(e.instagram);
                        return igData ? (
                          <a
                            href={igData.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-2 text-sm text-white/80 hover:text-[#E67E22] transition-colors mb-4"
                          >
                            <Instagram className="w-4 h-4" />
                            View Event
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : null;
                      })()}
                      
                      {/* Action Buttons */}
                      <div className="flex gap-2 mt-auto">
                        <button 
                          onClick={(evt) => {
                            evt.stopPropagation();
                            window.open(generateGoogleCalendarUrl(e), '_blank');
                          }}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-[#E67E22] text-white text-xs hover:bg-[#E67E22]/20 transition-all"
                        >
                          <Calendar className="w-3 h-3" />
                          Google
                        </button>
                        <button 
                          onClick={(evt) => {
                            evt.stopPropagation();
                            generateICalFile(e);
                          }}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-[#E67E22] text-white text-xs hover:bg-[#E67E22]/20 transition-all"
                        >
                          <Calendar className="w-3 h-3" />
                          iCal
                        </button>
                      </div>
                    </div>
                  </Tile>
                ));
              })}
              
              {(focusedEvents || searchFilteredEvents).length === 0 && (
                <div className="text-center text-white/50 py-8">
                  <p className="mb-2">No events found</p>
                  <button 
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedMonth(null);
                      setSelectedEventType(null);
                      setSelectedRegion(null);
                      setSelectedCountry(null);
                      setFocusedEvents(null);
                    }}
                    className="text-[#E67E22] text-sm hover:underline transition-all"
                  >
                    Clear filters
                  </button>
                </div>
              )}
            </div>
            
            {/* Footer - Results Count */}
            <div className="p-4 border-t border-white/10 bg-black/30">
              <div className="text-center text-white/60 text-sm">
                {focusedEvents 
                  ? `${focusedEvents.length} selected event${focusedEvents.length !== 1 ? 's' : ''}`
                  : `${searchFilteredEvents.length} event${searchFilteredEvents.length !== 1 ? 's' : ''} found`
                }
              </div>
            </div>
          </div>
        </div>
      ) : (
        // LIST VIEW
        <div className="flex-1 min-h-0 max-h-full overflow-y-auto px-4 md:px-8 lg:px-16 py-8">
          {/* Month-by-Month Event List */}
          <div className="max-w-7xl mx-auto space-y-12">
            {eventsByMonth.map(({ yearMonth, displayName, events }) => (
              <div key={yearMonth} className="space-y-4">
                {/* Month Header */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                  <h3 
                    className="text-3xl md:text-4xl font-bold text-white"
                    style={{ fontFamily: "'Bensoon', sans-serif" }}
                  >
                    <span style={{ color: "#E67E22" }}>{displayName}</span>
                  </h3>
                  <span className="text-white/60 text-lg">
                    ({events.length} event{events.length !== 1 ? 's' : ''})
                  </span>
                </div>

                {/* Events Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {events.map((event, index) => (
                    <Tile
                      key={`${event.id}-${index}`}
                      variant="glass"
                      size="md"
                      hoverable
                      noPadding
                      className="cursor-pointer hover:scale-[1.02] transition-transform"
                    >
                      <div className="p-4 flex flex-col h-full">
                        {/* Event Flyer */}
                        {event.flyer_url ? (
                          <div className="w-full h-56 mb-4 bg-black/30 rounded-lg overflow-hidden flex items-center justify-center">
                            <img
                              src={event.flyer_url}
                              alt={event.title}
                              className="w-full h-full object-contain"
                            />
                          </div>
                        ) : (
                          <div className="w-full h-56 mb-4 rounded-lg border border-[#E67E22]/30 bg-[#E67E22]/10 flex items-center justify-center">
                            <Calendar className="w-16 h-16 text-[#E67E22]/50" />
                          </div>
                        )}

                        {/* Title - Orange */}
                        <h4 className="font-bold text-xl mb-3 line-clamp-2" style={{ color: '#E67E22' }}>
                          {event.title}
                        </h4>
                        
                        {/* Date Range */}
                        <p className="text-sm text-white/80 mb-2 flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {formatDateRange(event.date_start, event.date_end)}
                        </p>
                        
                        {/* Location */}
                        <p className="text-sm text-white/80 mb-3 flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          {event.city}, {event.country}
                        </p>

                        {/* Event Type Badge */}
                        {event.event_type && (
                          <span className="inline-block px-3 py-1 text-xs rounded-full mb-4 self-start"
                            style={{ background: event.event_type === "Festival" ? "#9B59B6" : "#E67E22", color: 'white' }}>
                            {event.event_type}
                          </span>
                        )}
                        
                        {/* Instagram Link */}
                        {event.instagram && (() => {
                          const igData = normalizeInstagramUrl(event.instagram);
                          return igData ? (
                            <a
                              href={igData.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-2 text-sm text-white/80 hover:text-[#E67E22] transition-colors mb-4"
                            >
                              <Instagram className="w-4 h-4" />
                              View on Instagram
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : null;
                        })()}
                        
                        {/* Action Buttons */}
                        <div className="flex gap-2 mt-auto">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(generateGoogleCalendarUrl(event), '_blank');
                            }}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-[#E67E22] text-white hover:bg-[#E67E22]/20 transition-all"
                          >
                            <Calendar className="w-4 h-4" />
                            Google
                          </button>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              generateICalFile(event);
                            }}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-[#E67E22] text-white hover:bg-[#E67E22]/20 transition-all"
                          >
                            <Calendar className="w-4 h-4" />
                            iCal
                          </button>
                        </div>
                      </div>
                    </Tile>
                  ))}
                </div>
              </div>
            ))}

            {/* No Results Message */}
            {eventsByMonth.length === 0 && (
              <div className="text-center text-white/50 py-16">
                <p className="text-xl mb-4">No events found</p>
                <button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedMonth(null);
                      setSelectedEventType(null);
                      setSelectedRegion(null);
                      setSelectedCountry(null);
                    }}
                    className="text-[#E67E22] hover:underline transition-all"
                  >
                    Clear all filters
                  </button>
              </div>
            )}
          </div>
        </div>
      )}

      {clusterView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-lg" onClick={() => setClusterView(null)}>
          <div className="relative rounded-xl p-6 text-white shadow-2xl w-full max-w-4xl max-h-[80vh] overflow-auto bg-gradient-to-br from-[#141419]/98 to-[#282832]/98 border-2 border-[#E67E22]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Bensoon', sans-serif", color: "#E67E22" }}>
                  📍 {clusterView[0]?.city}, {clusterView[0]?.country}
                </h2>
                <p className="text-sm opacity-70" style={{ fontFamily: "'Bensoon', sans-serif" }}>
                  {clusterView.length} upcoming events
                </p>
              </div>
              <button onClick={() => setClusterView(null)} className="text-white/60 hover:text-white transition-colors text-3xl">×</button>
            </div>

            <div className="space-y-4">
              {clusterView.sort((a, b) => new Date(a.date_start).getTime() - new Date(b.date_start).getTime()).map((event, index) => (
                <div key={`${event.id}-${index}`} className="flex gap-4 p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-all border border-white/10">
                  {event.flyer_url && (
                    <img src={event.flyer_url} alt={event.title} className="w-24 h-24 object-cover rounded-lg border-2 border-[#E67E22]" />
                  )}
                  <div className="flex-1">
                    <h3 className="font-bold text-lg" style={{ color: "#E67E22" }}>{event.title}</h3>
                    <p className="text-sm opacity-80">📅 {new Date(event.date_start).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                    {event.event_type && (
                      <span className="inline-block mt-2 px-3 py-1 text-xs rounded-full" style={{ background: event.event_type === "Festival" ? "#E67E22" : "#9B59B6" }}>
                        {event.event_type}
                      </span>
                    )}
                    {event.instagram && (() => {
                      const igData = normalizeInstagramUrl(event.instagram);
                      return igData ? (
                        <a href={igData.url} target="_blank" rel="noopener noreferrer" className="block mt-2 text-xs text-blue-400 hover:underline">
                          {igData.display}
                        </a>
                      ) : null;
                    })()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedEvent && !selectedEvent.isCluster && (
        <div className="absolute bottom-8 left-8 rounded-xl p-4 text-white shadow-lg w-80 bg-gradient-to-br from-[#141419]/95 to-[#282832]/95 border border-[#E67E22] backdrop-blur-lg z-30">
          {selectedEvent.flyer_url && (
            <img src={selectedEvent.flyer_url} alt={selectedEvent.title} className="w-full h-48 object-cover rounded-lg mb-4 border-2 border-[#E67E22]" />
          )}
          <h3 className="font-bold text-xl mb-2" style={{ fontFamily: "'Bensoon', sans-serif", color: "#E67E22" }}>
            {selectedEvent.title}
          </h3>
          <p className="text-sm opacity-80 mb-2">📅 {new Date(selectedEvent.date_start).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
          <p className="text-sm opacity-80 mb-2">📍 {selectedEvent.city}, {selectedEvent.country}</p>
          {selectedEvent.event_type && (
            <span className="inline-block px-3 py-1 text-xs rounded-full mb-2" style={{ background: selectedEvent.event_type === "Festival" ? "#E67E22" : "#9B59B6" }}>
              {selectedEvent.event_type}
            </span>
          )}
          {selectedEvent.instagram && (() => {
            const igData = normalizeInstagramUrl(selectedEvent.instagram);
            return igData ? (
              <a href={igData.url} target="_blank" rel="noopener noreferrer" className="block text-sm text-blue-400 hover:underline mb-2">
                {igData.display}
              </a>
            ) : null;
          })()}
          <button onClick={() => setSelectedEvent(null)} className="absolute top-2 right-2 text-white/60 hover:text-white text-2xl transition-colors">×</button>
        </div>
      )}
    </div>
    </>
  );
}
